import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyFlexcolAlignSelf =
  | "auto"
  | "flex-start"
  | "center"
  | "flex-end"
  | "stretch"
  | "baseline";

/**
 * @element sky-flexcol
 *
 * @summary Flex column item for `sky-flexbox` layouts with responsive span controls.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/flexbox
 *
 * @slot - Default slot for column content.
 *
 * @property {number} span - Base number of columns to span.
 * @property {number | undefined} spanLg - Span at large breakpoints.
 * @property {number | undefined} spanMd - Span at medium breakpoints.
 * @property {number | undefined} spanSm - Span at small breakpoints.
 * @property {number | undefined} spanXs - Span at extra-small breakpoints.
 * @property {number} grow - Flex grow factor.
 * @property {number} shrink - Flex shrink factor.
 * @property {string | null} basis - Flex basis override.
 * @property {number | undefined} order - Flex order override.
 * @property {SkyFlexcolAlignSelf | undefined} alignSelf - Cross-axis alignment override.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method refreshStyles Re-applies CSS custom properties from current values.
 * @method getSpan Returns current base span value.
 * @method setSpan Updates base span value and refreshes styles.
 *
 * @csspart col - The flex item wrapper.
 *
 * @Behavior
 * - Designed to work within `<sky-flexbox>` containers
 * - Supports responsive column spans with breakpoint attributes
 * - Exposes all standard flex item properties
 * - Automatically syncs properties to CSS custom properties
 *
 * - `span`: Base span across columns (1 to container's column count)
 * - `span-lg`: Span at large screens (≤1200px)
 * - `span-md`: Span at medium screens (≤992px)
 * - `span-sm`: Span at small screens (≤768px)
 * - `span-xs`: Span at extra-small screens (≤576px)
 *
 * @example
 * ```html
 * <sky-flexbox col="12" gap="16px">
 *   <sky-flexcol span="8">
 *     <div class="card">Main Content</div>
 *   </sky-flexcol>
 *   <sky-flexcol span="4">
 *     <div class="card">Sidebar</div>
 *   </sky-flexcol>
 * </sky-flexbox>
 * ```
 * ```vue
 * <template>
 *   <sky-flexbox col="12" gap="16px">
 *     <sky-flexcol span="8" span-md="12">Main Content</sky-flexcol>
 *     <sky-flexcol span="4" span-md="12">Sidebar</sky-flexcol>
 *   </sky-flexbox>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-flexbox col="12" gap="16px">
 *       <sky-flexcol span="8" span-md="12">Main Content</sky-flexcol>
 *       <sky-flexcol span="4" span-md="12">Sidebar</sky-flexcol>
 *     </sky-flexbox>
 *   );
 * }
 * ```
 */
@customElement("sky-flexcol")
export class SkyFlexcol extends LitElement {
  /**
   * Number of columns to span.
   * @public
   */
  @property({ type: Number })
  span = 1;

  /**
   * Columns spanned at large screens (≤1200px).
   * @public
   */
  @property({ type: Number })
  spanLg?: number;

  /**
   * Columns spanned at medium screens (≤992px).
   * @public
   */
  @property({ type: Number })
  spanMd?: number;

  /**
   * Columns spanned at small screens (≤768px).
   * @public
   */
  @property({ type: Number })
  spanSm?: number;

  /**
   * Columns spanned at extra-small screens (≤576px).
   * @public
   */
  @property({ type: Number })
  spanXs?: number;

  /**
   * Flex grow factor.
   * @public
   */
  @property({ type: Number })
  grow = 0;

  /**
   * Flex shrink factor.
   * @public
   */
  @property({ type: Number })
  shrink = 0;

  /**
   * Flex basis (e.g., `"200px"` or `"auto"`).
   * @public
   */
  @property({ type: String })
  basis: string | null = null;

  /**
   * Flex order relative to siblings.
   * @public
   */
  @property({ type: Number })
  order?: number;

  /**
   * Self alignment override.
   * @public
   */
  @property({ type: String })
  alignSelf?: SkyFlexcolAlignSelf;

static override styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      /* These are read by the container’s ::slotted(*) rules */
      flex-grow: var(--col-grow, 0);
      flex-shrink: var(--col-shrink, 0);
      order: var(--col-order, 0);
      align-self: var(--col-align-self, auto);
      /* If you set basis, it will override the basis calc coming from container's ::slotted(*) */
      flex-basis: var(--col-basis, auto);
    }
  `;

  /** @protected */

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  override firstUpdated() {
    this.syncVars();
  }

  /** @protected */
  override updated(changed: Map<string | number | symbol, unknown>) {
    if (
      changed.has("span") ||
      changed.has("spanLg") ||
      changed.has("spanMd") ||
      changed.has("spanSm") ||
      changed.has("spanXs") ||
      changed.has("grow") ||
      changed.has("shrink") ||
      changed.has("basis") ||
      changed.has("order") ||
      changed.has("alignSelf")
    ) {
      this.syncVars();
    }
  }

  /**
   * Syncs component properties to CSS custom properties.
   * @private
   */
  private syncVars() {
    // Span properties
    this.style.setProperty("--span", String(this.span));
    if (this.spanLg) this.style.setProperty("--span-lg", String(this.spanLg));
    if (this.spanMd) this.style.setProperty("--span-md", String(this.spanMd));
    if (this.spanSm) this.style.setProperty("--span-sm", String(this.spanSm));
    if (this.spanXs) this.style.setProperty("--span-xs", String(this.spanXs));

    // Flex properties
    this.style.setProperty("--col-grow", String(this.grow));
    this.style.setProperty("--col-shrink", String(this.shrink));

    if (this.order != null) {
      this.style.setProperty("--col-order", String(this.order));
    } else {
      this.style.removeProperty("--col-order");
    }

    if (this.alignSelf) {
      this.style.setProperty("--col-align-self", this.alignSelf);
    } else {
      this.style.removeProperty("--col-align-self");
    }

    if (this.basis) {
      this.style.setProperty("--col-basis", this.basis);
    } else {
      this.style.removeProperty("--col-basis");
    }
  }

  /**
   * Forces resynchronization of CSS custom properties.
   * Useful when properties are changed programmatically.
   * @public
   */
  public refreshStyles() {
    this.syncVars();
  }

  /**
   * Gets the current span value.
   * @returns Current span value
   * @public
   */
  public getSpan(): number {
    return this.span;
  }

  /**
   * Sets the span value and updates styles.
   * @param value - New span value
   * @public
   */
  public setSpan(value: number): void {
    this.span = value;
    this.syncVars();
  }

  /** @protected */
  override render() {
    return html` <slot part="col"></slot> `;
  }
}
